import { ReuseModal } from "../../ReuseModal";
import { useEffect, useMemo, useState } from "react";
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { HubSpotIcon, MarketoIcon, SalesForceIcon } from "../../../images";
import {
  CRMData,
  CRMListItem,
  IntegrationType,
} from "../../../../types/apiTypes";
import { DataSources } from "./DataSource";
import { CrmTypes } from "../../../../types";
import { capitalizeFirstLetter } from "../../../../utils/capitalizeFirstLetter";

const UPLOAD_OPTIONS = ["All Data", "Choose from List"];
const CRM_OPTIONS = [
  { value: "hubspot", icon: <HubSpotIcon /> },
  { value: "salesforce", icon: <SalesForceIcon /> },
  { value: "marketo", icon: <MarketoIcon /> },
];

type Props = {
  openModal: IntegrationType;
  onClose: () => void;
  setDataSource: (data: CRMData) => void;
  crmList: CRMListItem[];
  setOpenModal: (data: CrmTypes) => void;
  isLoading: boolean;
};

export const SyncCRMModal = ({
  openModal,
  onClose,
  setDataSource,
  crmList,
  setOpenModal,
  isLoading,
}: Props) => {
  const [selectedData, setSelectedData] = useState<string>("");
  const [uploadOption, setUploadOption] = useState<string>(UPLOAD_OPTIONS[1]);

  const crmOption = useMemo(
    () => CRM_OPTIONS.find(({ value }) => value === openModal),
    [openModal, CRM_OPTIONS],
  );

  useEffect(() => {
    if (crmOption?.value !== openModal)
      setOpenModal((crmOption?.value as CrmTypes) || openModal);
  }, [crmOption]);

  useEffect(() => {
    if (uploadOption === UPLOAD_OPTIONS[0]) setSelectedData("");
  }, [uploadOption]);

  const syncHandler = () => {
    setDataSource({ crm: openModal, list_id: selectedData || undefined });
    onClose();
  };

  const isMarketo = openModal === "marketo";

  return (
    <ReuseModal
      isOpen={!!openModal}
      onClose={onClose}
      title={isMarketo ? "Import Data" : "Sync CRM"}
      onSubmit={syncHandler}
      maxWidth="540px"
      submitBtnColor="#1C3C6C"
      submitBtnText={isMarketo ? "Import" : "Sync"}
      isLoading={isLoading}
      isDisabled={isLoading}
      isDisabledCancelBtn={isLoading}
    >
      <Stack gap="16px">
        <Stack
          flexDirection={"row"}
          gap={"6px"}
          alignItems={"center"}
          padding={"16px 0 8px 0"}
        >
          {crmOption?.icon}
          <Typography
            variant="body1"
            color={"custom.textDarkBlue"}
            sx={{ color: "#232323", fontWeight: 500 }}
          >
            {capitalizeFirstLetter(crmOption?.value || "")}
          </Typography>
        </Stack>
        <Typography
          component="span"
          variant="subtitle1"
          color="custom.textDarkBlue"
          fontWeight={500}
        >
          Which data-set would you like to import?
        </Typography>
        <FormControl sx={{ padding: "0 12px", alignItems: "flex-start" }}>
          <RadioGroup
            aria-labelledby="demo-radio-buttons-group-label"
            defaultValue={uploadOption}
            value={uploadOption}
            name="radio-buttons-group"
            onChange={(e, el) => setUploadOption(el)}
          >
            {UPLOAD_OPTIONS.map((option) => (
              <FormControlLabel
                key={option}
                value={option}
                control={<Radio />}
                label={option}
                disabled={isLoading}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </Stack>

      {uploadOption === UPLOAD_OPTIONS[1] &&
        (crmList.length === 0 ? (
          <Typography variant="subtitle1" color="#1F293299" align="center">
            You don't have any list
          </Typography>
        ) : (
          <DataSources
            crmList={crmList}
            setSelectedData={setSelectedData}
            isDisabled={isLoading}
          />
        ))}
    </ReuseModal>
  );
};
